/**
 * TeamBillingSection: staff-only, read-only view of a team's billing
 * config and its unpriced gap (TRA-1163, TRA-875 phase 1).
 *
 * Answers "why is this team's spend blank?" without dumping KV: the mode,
 * the Stripe ids, every priced model with its traffic in the window (its
 * own rate or the catalog list price it inherits, TRA-1282), and the two
 * gaps (traffic without a rate, and routes with neither a rate nor
 * traffic). Editing rates is TRA-875 phase 2 and stays a gateway PUT.
 *
 * Wraps GET /api/internal/teams/{id}/billing. Its query key sits under
 * the staff team list's, so invalidating the list refreshes it too.
 */

import { useQuery } from '@tanstack/react-query'

import { customFetch, getApiErrorDetail } from '@/api/client'
import type {
  ConfiguredModelView,
  TeamBillingAdminResponse,
  TeamBillingAdminResponseMode,
  UnpricedModelView,
} from '@/api/generated/model'
import { formatCompact } from '@/components/models/home/contracts'
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader'
import { SettingsBlock, SettingsSection } from '@/components/settings/SettingsSection'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { INTERNAL_TEAMS_QUERY_KEY } from '@/hooks/useInternalTeams'

import type { TeamConfigSectionProps } from './sections'
import {
  LIST_PRICE_BADGE,
  MODE_DESCRIPTIONS,
  MODE_LABELS,
  listPriceSourceLabel,
  modelLabel,
  pricedModelsDescription,
  pricingLabel,
  rateLabel,
  spendLabel,
  unpricedRequestsLabel,
} from './teamBilling'

const MODE_BADGE: Record<TeamBillingAdminResponseMode, BadgeProps['variant']> = {
  unconfigured: 'outline',
  blacklisted: 'secondary',
  display_only: 'secondary',
  billable: 'success',
}

export function TeamBillingSection({ section, team }: TeamConfigSectionProps): React.JSX.Element {
  const billingQuery = useQuery({
    queryKey: [...INTERNAL_TEAMS_QUERY_KEY, team.id, 'billing'],
    queryFn: () =>
      customFetch<TeamBillingAdminResponse>(`/api/internal/teams/${team.id}/billing`),
    retry: false,
  })
  const mode = billingQuery.data?.mode

  return (
    <div>
      <SettingsPageHeader
        section={section}
        badge={
          mode !== undefined ? (
            <Badge variant={MODE_BADGE[mode]}>{MODE_LABELS[mode]}</Badge>
          ) : undefined
        }
      />
      {billingQuery.isLoading ? (
        <SettingsBlock>
          <Skeleton className="h-24 w-full" />
        </SettingsBlock>
      ) : billingQuery.isError ? (
        <SettingsBlock>
          <p className="text-sm text-destructive" role="alert">
            {getApiErrorDetail(billingQuery.error) ?? 'Failed to load the billing config.'}
          </p>
        </SettingsBlock>
      ) : billingQuery.data ? (
        <BillingDetails billing={billingQuery.data} />
      ) : null}
    </div>
  )
}

function BillingDetails({ billing }: { billing: TeamBillingAdminResponse }): React.JSX.Element {
  return (
    <>
      <SettingsSection title="Account" description={MODE_DESCRIPTIONS[billing.mode]}>
        <AccountFacts billing={billing} />
      </SettingsSection>
      <PricedModels rows={billing.models} />
      <UnpricedTraffic rows={billing.unpriced_with_traffic} />
      <UnpricedRoutes routes={billing.unpriced_routes} />
    </>
  )
}

function AccountFacts({ billing }: { billing: TeamBillingAdminResponse }): React.JSX.Element {
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-sm">
      <Field label="Gateway team" value={billing.gateway_team_id} />
      {billing.mode === 'billable' && (
        <>
          <Field label="Stripe customer" value={billing.stripe_customer_id} />
          <Field label="Stripe subscription" value={billing.stripe_subscription_id} />
          <Field label="Floor date" value={billing.floor_date} />
        </>
      )}
      {billing.gpu && (
        <>
          <Field label="GPU meter" value={billing.gpu.meter_event_name} />
          <Field label="Dedicated GPUs" value={String(billing.gpu.dedicated_gpus)} />
          <Field label="Baseten model ids" value={billing.gpu.model_ids.join(', ')} />
        </>
      )}
    </dl>
  )
}

function Field({ label, value }: { label: string; value: string | null }): React.JSX.Element {
  return (
    <>
      <dt className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="break-all font-mono text-xs text-foreground">{value ?? '—'}</dd>
    </>
  )
}

function PricedModels({ rows }: { rows: ConfiguredModelView[] }): React.JSX.Element {
  return (
    <SettingsSection title="Priced models" description={pricedModelsDescription(rows)}>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No models are priced.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Model</TableHead>
              <TableHead>Pricing</TableHead>
              <TableHead className="text-right">Requests (30d)</TableHead>
              <TableHead className="text-right">Spend (30d)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.model}>
                <TableCell className="font-mono text-xs">{row.model}</TableCell>
                <TableCell className="font-mono text-xs">
                  <PricingCell row={row} />
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {row.window_request_count.toLocaleString('en-US')}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">{spendLabel(row)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </SettingsSection>
  )
}

function PricingCell({ row }: { row: ConfiguredModelView }): React.JSX.Element {
  const { pricing } = row
  const source = listPriceSourceLabel(row)
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span>{pricingLabel(pricing)}</span>
        {row.source === 'catalog' && <Badge variant="outline">{LIST_PRICE_BADGE}</Badge>}
        {pricing.kind === 'hourly' && <Badge variant="outline">hourly</Badge>}
        {pricing.kind === 'token' && pricing.rates.display_only && (
          <Badge variant="secondary">display only</Badge>
        )}
      </div>
      {source !== null && <div className="text-muted-foreground">{source}</div>}
      {pricing.upstreams.map((override) => (
          <div key={override.upstream_id} className="flex items-center gap-2 text-muted-foreground">
            <span>
              via {override.upstream_id}: {rateLabel(override)}
            </span>
            {override.display_only && <Badge variant="secondary">display only</Badge>}
          </div>
        ))}
    </div>
  )
}

function UnpricedTraffic({ rows }: { rows: UnpricedModelView[] }): React.JSX.Element {
  return (
    <SettingsSection
      title="Traffic without a rate"
      description="These requests had no rate. They are never invoiced, and the nightly cycle alerts on them."
    >
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Every request in the window has a rate.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Model</TableHead>
              <TableHead className="text-right">Unpriced requests (30d)</TableHead>
              <TableHead className="text-right">Model tokens (30d)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.model}>
                <TableCell className="font-mono text-xs">
                  <div>{modelLabel(row.model)}</div>
                  {row.billed_models.length > 0 && (
                    <div className="text-muted-foreground">No rate for {row.billed_models.join(', ')}</div>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">{unpricedRequestsLabel(row)}</TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {formatCompact(row.window_total_tokens)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </SettingsSection>
  )
}

function UnpricedRoutes({ routes }: { routes: string[] }): React.JSX.Element {
  return (
    <SettingsSection
      title="Routes without a rate or traffic"
      description="A customer could call these tomorrow, and the usage would bill as unconfigured."
    >
      {routes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Every route has a rate or traffic in the window.
        </p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {routes.map((route) => (
            <li key={route}>
              <Badge variant="outline" className="normal-case tracking-normal">
                {route}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </SettingsSection>
  )
}
